/**
 * @file waiterOrders.service.ts
 * @description Dedicated service for fetching and managing active waiter orders,
 * fully compatible with Public Menu order placement, table requests, and Stripe online payments.
 * 
 * ARCHITECTURAL RESPONSIBILITIES:
 * 1. Queries orders across all pipeline statuses: new, preparing, ready, done.
 * 2. Correlates department, allergens, guest assistance calls, and online payment details.
 * 3. Provides atomic status mutations:
 *    - Confirm to Kitchen (new -> preparing)
 *    - Serve to Table (ready -> completed/done)
 *    - Collect Payment (unpaid -> paid)
 *    - Resolve Guest Assistance Call
 */

import { supabase } from '../../../config/supabase.config';
import type {
  WaiterActiveOrder,
  WaiterOrderItem,
  PublicMenuOrigin,
  PublicMenuGuestAction,
  ServiceMode,
  OrderPipelineStatus,
} from '../../../types/waiterOrder.types';

/**
 * Normalizes any format of table string from Public Menu URLs or QR codes
 * e.g., "Table 4", "table-4", "t-04", "4" -> { key: "4", display: "Table 4" }
 */
export const normalizeTableIdentifier = (
  raw: any
): { key: string; display: string; isNumeric: boolean } => {
  if (!raw) return { key: '', display: 'N/A', isNumeric: false };
  const str = String(raw).trim();
  const stripped = str.replace(/^(table|t)[\s\-_]*/i, '').trim();
  const isNum = /^\d+$/.test(stripped);

  if (!stripped) {
    return { key: str.toLowerCase(), display: str, isNumeric: false };
  }

  const display = isNum ? `Table ${parseInt(stripped, 10)}` : `Table ${stripped}`;
  return {
    key: stripped.toLowerCase(),
    display,
    isNumeric: isNum,
  };
};

interface CachedVenueMeta {
  currency: string;
  sectionByTableKey: Map<string, string>;
  departmentNameById: Map<string, string>;
  timestamp: number;
}

const VENUE_META_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes cache for static venue layout & currency
const venueMetaCache = new Map<string, CachedVenueMeta>();

/**
 * Fetches orders for a venue across all pipeline statuses (new, preparing, ready, done).
 */
export const fetchActiveWaiterOrders = async (venueId: string): Promise<WaiterActiveOrder[]> => {
  // Restricts active orders to the current 24-hour service window to prevent stale unfulfilled orders from lingering
  const cutoffIso = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  const now = Date.now();
  const cachedMeta = venueMetaCache.get(venueId);
  const isMetaCacheValid = Boolean(cachedMeta && now - cachedMeta.timestamp < VENUE_META_CACHE_TTL_MS);

  // If venue layout and currency are cached, fetch only orders and pending assistance calls (50% query reduction)
  const ordersPromise = supabase
    .from('orders')
    .select(`
      id,
      venue_id,
      table_id,
      status,
      priority,
      total_amount,
      type,
      waiter_id,
      payment_status,
      payment_method,
      bill_requested_at,
      confirmed_to_kitchen_at,
      created_at,
      served_at,
      metadata,
      order_items (
        id,
        order_id,
        item_id,
        price,
        quantity,
        notes,
        status,
        kitchen_station,
        course,
        created_at,
        selected_modifiers,
        items (
          name,
          kitchen_station
        )
      )
    `)
    .eq('venue_id', venueId)
    .in('status', ['pending', 'confirmed', 'preparing', 'ready', 'completed'])
    .gte('created_at', cutoffIso)
    .order('created_at', { ascending: false })
    .limit(100);

  const requestsPromise = supabase
    .from('waiter_requests')
    .select('id, table_id, type, status, metadata, created_at')
    .eq('venue_id', venueId)
    .eq('status', 'pending')
    .neq('type', 'cash')
    .gte('created_at', cutoffIso)
    .order('created_at', { ascending: false });

  let fallbackCurrency = 'AED';
  let sectionByTableKey = new Map<string, string>();
  let departmentNameById = new Map<string, string>();

  let ordersData: any[] | null = null;
  let requestsData: any[] | null = null;

  if (isMetaCacheValid && cachedMeta) {
    fallbackCurrency = cachedMeta.currency;
    sectionByTableKey = cachedMeta.sectionByTableKey;
    departmentNameById = cachedMeta.departmentNameById;

    const [ordersRes, requestsRes] = await Promise.all([ordersPromise, requestsPromise]);
    if (ordersRes.error) {
      console.error('[waiterOrders.service] Orders fetch failed:', ordersRes.error);
      throw ordersRes.error;
    }
    if (requestsRes.error) {
      console.warn('[waiterOrders.service] Waiter requests fetch warning:', requestsRes.error);
    }
    ordersData = ordersRes.data;
    requestsData = requestsRes.data;
  } else {
    const [ordersRes, requestsRes, tablesRes, venueRes, deptRes] = await Promise.all([
      ordersPromise,
      requestsPromise,
      supabase
        .from('reservation_tables')
        .select('id, name, section:reservation_sections(name)')
        .eq('venue_id', venueId),
      supabase
        .from('venues')
        .select('currency')
        .eq('id', venueId)
        .maybeSingle(),
      supabase
        .from('departments')
        .select('id, name')
        .eq('venue_id', venueId),
    ]);

    if (ordersRes.error) {
      console.error('[waiterOrders.service] Orders fetch failed:', ordersRes.error);
      throw ordersRes.error;
    }
    if (requestsRes.error) {
      console.warn('[waiterOrders.service] Waiter requests fetch warning:', requestsRes.error);
    }

    ordersData = ordersRes.data;
    requestsData = requestsRes.data;
    fallbackCurrency = venueRes.data?.currency || 'AED';

    // Build lookup map for table sections
    for (const t of (tablesRes.data || []) as any[]) {
      const tableIdentifier = t.name || t.table_number;
      const sectionName = t.section?.name || t.section || 'Main Dining';
      if (tableIdentifier) {
        const { key } = normalizeTableIdentifier(tableIdentifier);
        sectionByTableKey.set(key, sectionName);
        sectionByTableKey.set(String(tableIdentifier).toLowerCase(), sectionName);
      }
    }

    // Build lookup map for human-readable department names by UUID
    for (const d of (deptRes.data || []) as any[]) {
      if (d.id && d.name) {
        departmentNameById.set(d.id, d.name);
        departmentNameById.set(d.id.toLowerCase(), d.name);
      }
    }

    venueMetaCache.set(venueId, {
      currency: fallbackCurrency,
      sectionByTableKey,
      departmentNameById,
      timestamp: now,
    });
  }

  // Build lookup map for pending waiter requests
  const requestsByTableKey = new Map<string, any[]>();
  for (const req of requestsData || []) {
    if (req.table_id) {
      const { key } = normalizeTableIdentifier(req.table_id);
      const rawKey = String(req.table_id).toLowerCase().trim();

      const list = requestsByTableKey.get(key) || [];
      list.push(req);
      requestsByTableKey.set(key, list);

      if (rawKey !== key) {
        const rawList = requestsByTableKey.get(rawKey) || [];
        rawList.push(req);
        requestsByTableKey.set(rawKey, rawList);
      }
    }
  }

  // Excludes unconfirmed online checkout sessions so staff only receives paid online orders
  const confirmedOrdersData = (ordersData || []).filter(
    (row: any) => !(row.payment_method === 'ONLINE' && row.payment_status !== 'paid')
  );

  return confirmedOrdersData.map((row: any): WaiterActiveOrder => {
    const meta = row.metadata || {};

    // 1. Resolve service mode
    let serviceMode: ServiceMode = 'dine-in';
    const rawMode = String(meta.service_mode || row.type || '').toLowerCase();
    if (rawMode === 'pickup') {
      serviceMode = 'pickup';
    } else if (rawMode === 'delivery') {
      serviceMode = 'delivery';
    } else {
      serviceMode = 'dine-in';
    }

    // 2. Resolve table number and display name
    const rawTable = meta.table_number || row.table_id;
    const { key: tableKey, display: tableDisplay } = normalizeTableIdentifier(rawTable);

    let finalTableNumber = tableDisplay;
    if (serviceMode === 'pickup') {
      finalTableNumber = `Pickup • ${meta.customer_name || 'Guest'}`;
    } else if (serviceMode === 'delivery') {
      finalTableNumber = `Delivery • ${meta.customer_name || 'Guest'}`;
    } else if (tableDisplay === 'N/A' && meta.customer_name) {
      finalTableNumber = `Dine-In • ${meta.customer_name}`;
    }

    const orderNumber = row.id ? `#${row.id.slice(-4).toUpperCase()}` : '#0000';
    const section =
      sectionByTableKey.get(tableKey) ||
      sectionByTableKey.get(String(rawTable).toLowerCase()) ||
      meta.section ||
      'Main Dining';

    // 3. Resolve human-readable department name and UUID
    const rawDept =
      meta.department_name ||
      meta.department ||
      meta.department_id ||
      null;

    let departmentName: string | null = null;
    let departmentId: string | null = null;

    if (rawDept) {
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(rawDept).trim());
      if (isUuid) {
        departmentId = String(rawDept).trim();
        departmentName = departmentNameById.get(departmentId) || departmentNameById.get(departmentId.toLowerCase()) || null;
      } else {
        departmentName = String(rawDept).trim();
        if (meta.department_id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(meta.department_id).trim())) {
          departmentId = String(meta.department_id).trim();
        }
      }
    }

    // 4. Resolve pipeline status: new | preparing | ready | done
    let pipelineStatus: OrderPipelineStatus = 'new';
    if (row.status === 'preparing') {
      pipelineStatus = 'preparing';
    } else if (row.status === 'ready') {
      pipelineStatus = 'ready';
    } else if (row.status === 'completed') {
      pipelineStatus = 'done';
    } else {
      pipelineStatus = 'new';
    }

    // 5. Resolve Public Menu Touchpoint (Guest)
    const isPublicMenu =
      !row.waiter_id ||
      meta.origin === 'public_menu' ||
      meta.source === 'public_menu' ||
      Boolean(meta.guest_access_token_hash);
    const origin: PublicMenuOrigin = isPublicMenu ? 'public_menu' : 'waiter';

    // Check correlated requests from Public Menu (excluding cash order payment requests)
    const matchingReqs = (
      requestsByTableKey.get(tableKey) ||
      (row.table_id ? requestsByTableKey.get(String(row.table_id).toLowerCase()) : null) ||
      []
    ).filter((r: any) => String(r.type || '').toLowerCase().trim() !== 'cash');
    const activeReq = matchingReqs[0] || null;

    let guestAction: PublicMenuGuestAction = 'idle';
    let guestActionId: string | null = null;
    let guestActionCreatedAt: string | null = null;

    if (row.payment_method === 'ONLINE' && row.payment_status === 'paid') {
      guestAction = 'paid_online';
    } else if (activeReq) {
      guestActionId = activeReq.id;
      guestActionCreatedAt = activeReq.created_at;
      const reqType = String(activeReq.type).toLowerCase().trim();
      if (reqType === 'bill') {
        guestAction = 'bill_requested';
      } else if (reqType === 'water') {
        guestAction = 'water_requested';
      } else if (reqType === 'clean') {
        guestAction = 'clean_requested';
      } else {
        guestAction = 'call_requested';
      }
    } else if (row.bill_requested_at) {
      guestAction = 'bill_requested';
    }

    // 6. Elapsed time and formatted date
    const createdAtDate = new Date(row.created_at || '');
    const createdAtMs = createdAtDate.getTime();
    const elapsedMinutes = !isNaN(createdAtMs)
      ? Math.max(0, Math.floor((Date.now() - createdAtMs) / 60000))
      : 0;

    let formattedDate = 'Today';
    if (!isNaN(createdAtMs)) {
      const timeStr = createdAtDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      const dateStr = createdAtDate.toLocaleDateString([], { month: 'short', day: 'numeric' });
      formattedDate = `${dateStr} • ${timeStr}`;
    }

    // 7. Allergens resolution
    const allergens: string[] = Array.isArray(meta.allergens)
      ? meta.allergens
      : typeof meta.allergens === 'string'
      ? meta.allergens.split(',').map((s: string) => s.trim()).filter(Boolean)
      : [];

    const currency = meta.display_currency || fallbackCurrency;

    // 8. Map line items
    const items: WaiterOrderItem[] = (row.order_items || []).map((oi: any) => ({
      id: oi.id,
      order_id: oi.order_id,
      name: oi.items?.name || oi.name || 'Menu Item',
      quantity: Number(oi.quantity) || 1,
      price: Number(oi.price) || 0,
      notes: oi.notes || null,
      modifiers: Array.isArray(oi.selected_modifiers) && oi.selected_modifiers.length > 0
        ? oi.selected_modifiers
        : (meta.modifiers || []),
      course: oi.course || 'main',
      kitchen_station: oi.kitchen_station || oi.items?.kitchen_station || null,
      allergens: oi.items?.allergens || [],
      status: oi.status || 'pending',
    }));

    return {
      id: row.id,
      venue_id: row.venue_id,
      table_id: row.table_id,
      table_number: finalTableNumber,
      raw_table_id: rawTable ? String(rawTable) : null,
      department: departmentName,
      department_id: departmentId,
      section,
      order_number: orderNumber,
      created_at: row.created_at,
      formatted_date: formattedDate,
      elapsed_minutes: elapsedMinutes,
      total_amount: Number(row.total_amount) || 0,
      currency,
      pipeline_status: pipelineStatus,
      is_confirmed_to_kitchen: Boolean(row.status === 'confirmed' || row.confirmed_to_kitchen_at || meta.confirmed_to_kitchen),
      confirmed_to_kitchen_at: row.confirmed_to_kitchen_at || meta.confirmed_to_kitchen_at || null,
      origin,
      service_mode: serviceMode,
      guest_name: meta.customer_name || null,
      customer_phone: meta.customer_phone || null,
      guest_count: Number(meta.guest_count) || (serviceMode === 'dine-in' ? 2 : 1),
      customer_notes: meta.notes || null,
      allergens,
      guest_action: guestAction,
      guest_action_id: guestActionId,
      guest_action_created_at: guestActionCreatedAt,
      ready_at: (row as any).ready_at || meta.ready_at || null,
      served_at: row.served_at || null,
      waiter_id: row.waiter_id,
      waiter_name: meta.waiter_name || null,
      payment_status: row.payment_status || 'unpaid',
      payment_method: row.payment_method || null,
      items,
    };
  });
};

/**
 * Confirms a new order and sends it to the kitchen queue ('confirmed').
 * The kitchen displays this ticket as confirmed so the chef can click 'START PREP'.
 */
export const confirmOrderToKitchen = async (orderId: string): Promise<void> => {
  const now = new Date().toISOString();
  const { data: { user } } = await supabase.auth.getUser();

  const updates: Record<string, any> = {
    status: 'confirmed',
    confirmed_to_kitchen_at: now,
  };
  if (user?.id) {
    updates.waiter_id = user.id;
  }

  const { error } = await supabase
    .from('orders')
    .update(updates)
    .eq('id', orderId);

  if (error) {
    console.error('[waiterOrders.service] Confirm to kitchen error:', error);
    throw error;
  }

  // Also update line items status to sent_to_kitchen
  await supabase
    .from('order_items')
    .update({
      status: 'sent_to_kitchen',
      sent_to_kitchen_at: now,
    })
    .eq('order_id', orderId);
};

/**
 * Marks an order as delivered/served to the table by the waiter ('completed').
 */
export const serveOrderToTable = async (orderId: string): Promise<void> => {
  const { data: { user } } = await supabase.auth.getUser();
  const updates: Record<string, any> = {
    status: 'completed',
    served_at: new Date().toISOString(),
  };
  if (user?.id) {
    updates.waiter_id = user.id;
  }

  const { error } = await supabase
    .from('orders')
    .update(updates)
    .eq('id', orderId);

  if (error) {
    console.error('[waiterOrders.service] Serve order error:', error);
    throw error;
  }
};

/**
 * Records payment for an order.
 *
 * CARD: marks the order as paid but does NOT close it (status unchanged).
 *       The waiter must still confirm the order as served separately.
 * CASH: marks paid and closes the order immediately (completed + served_at).
 */
export const collectOrderPayment = async (
  orderId: string,
  paymentMethod: 'CASH' | 'CARD' | 'ONLINE' = 'CASH'
): Promise<void> => {
  const now = new Date().toISOString();

  const updates: Record<string, any> = {
    payment_status: 'paid',
    payment_method: paymentMethod,
  };

  // Card payment: record that it has been paid but keep the order open so
  // the waiter still needs to confirm the food has been served to the table.
  if (paymentMethod === 'CASH') {
    updates.status = 'completed';
    updates.served_at = now;
  }

  const { error } = await supabase
    .from('orders')
    .update(updates)
    .eq('id', orderId);

  if (error) {
    console.error('[waiterOrders.service] Collect payment error:', error);
    throw error;
  }
};

/**
 * Resolves a pending guest assistance call from the public menu.
 */
export const resolveGuestCall = async (requestId: string): Promise<void> => {
  const { error } = await supabase
    .from('waiter_requests')
    .update({
      status: 'completed',
    })
    .eq('id', requestId);

  if (error) {
    console.error('[waiterOrders.service] Resolve guest call error:', error);
    throw error;
  }
};

/**
 * Cancels and voids an order along with its active line items and associated requests.
 */
export const cancelOrder = async (
  orderId: string,
  reason: string = 'Staff cancelled order'
): Promise<void> => {
  const now = new Date().toISOString();

  // 1. Fetch current order metadata to preserve existing properties
  const { data: existing } = await supabase
    .from('orders')
    .select('metadata')
    .eq('id', orderId)
    .maybeSingle();

  const currentMeta = (existing?.metadata && typeof existing.metadata === 'object') ? existing.metadata : {};
  const updatedMeta = {
    ...currentMeta,
    cancelled_at: now,
    cancelled_reason: reason,
  };

  // 2. Mark order as cancelled
  const { error: orderError } = await supabase
    .from('orders')
    .update({
      status: 'cancelled',
      metadata: updatedMeta,
    })
    .eq('id', orderId);

  if (orderError) {
    console.error('[waiterOrders.service] Cancel order error:', orderError);
    throw orderError;
  }

  // 3. Mark active line items as cancelled
  await supabase
    .from('order_items')
    .update({
      status: 'cancelled',
      cancelled_reason: reason,
    })
    .eq('order_id', orderId)
    .in('status', ['pending', 'sent_to_kitchen', 'preparing']);

  // 4. Resolve any pending waiter requests linked to this order
  await supabase
    .from('waiter_requests')
    .update({ status: 'completed' })
    .eq('metadata->>order_id', orderId)
    .eq('status', 'pending');
};
